import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2, UserPlus, ChevronLeft } from "lucide-react";
import { SearchSelect } from "@/components/filters/FilterMultiSelect";
import SupportEscalationSection, { emptyEscalation, createEscalationTicket } from "@/components/support/SupportEscalationSection";

export const POSTES = ["Commis", "Second", "Chef", "Chef confirmée", "Multisite", "Intérimaire"];
export const CONTRATS = ["CDI", "CDD", "INTERIM"];
export const HEURES = [40, 37.5, 35, 24];
export const DELAIS = [["immediat", "Immédiat"], ["15j", "À 15 jours"], ["30j", "À 30 jours"], ["45j", "À 45 jours"]];

const emptyItem = () => ({ corner_key: "", poste: "", contrat: "CDI", heures: 35, nb_postes: 1, delai: "immediat", description: "" });
const lbl = "text-[11px] font-bold uppercase text-slate-500 block mb-1";
const inp = "w-full text-sm px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-400";

function ItemRow({ idx, item, onChange, onRemove, corners, canRemove }) {
  const set = (k, v) => onChange({ ...item, [k]: v });
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/60 space-y-3" data-testid={`rcf-item-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-700">Demande n°{idx + 1}</span>
        {canRemove && <button type="button" onClick={onRemove} data-testid={`rcf-item-remove-${idx}`} className="text-[11px] text-rose-600 hover:text-rose-800 inline-flex items-center gap-1"><Trash2 className="w-3 h-3"/> Retirer</button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="col-span-full md:col-span-1">
          <label className={lbl}>Corner *</label>
          <SearchSelect value={item.corner_key} onChange={v => set("corner_key", v)} options={corners.map(c => ({ value: c.corner_key, label: c.corner || c.corner_key }))} placeholder="— Choisir un corner —" required data-testid={`rcf-corner-${idx}`} size="lg" menuWidth={260}/>
        </div>
        <div>
          <label className={lbl}>Type de poste *</label>
          <SearchSelect value={item.poste} onChange={v => set("poste", v)} options={POSTES.map(p => ({ value: p, label: p }))} placeholder="— Poste —" required data-testid={`rcf-poste-${idx}`} size="lg" menuWidth={220}/>
        </div>
        <div>
          <label className={lbl}>Type de contrat</label>
          <SearchSelect value={item.contrat} onChange={v => set("contrat", v)} options={CONTRATS.map(c => ({ value: c, label: c }))} data-testid={`rcf-contrat-${idx}`} size="lg" menuWidth={200}/>
        </div>
        <div>
          <label className={lbl}>Nombre d'heures</label>
          <SearchSelect value={String(item.heures)} onChange={v => set("heures", parseFloat(v))} options={HEURES.map(h => ({ value: String(h), label: `${String(h).replace(".", ",")} h` }))} data-testid={`rcf-heures-${idx}`} size="lg" menuWidth={200}/>
        </div>
        <div>
          <label className={lbl}>Nombre de postes</label>
          <input type="number" min="1" value={item.nb_postes} onChange={e => set("nb_postes", parseInt(e.target.value) || 1)} data-testid={`rcf-nb-${idx}`} className={inp}/>
        </div>
        <div>
          <label className={lbl}>Date du besoin</label>
          <SearchSelect value={item.delai} onChange={v => set("delai", v)} options={DELAIS.map(([v, l]) => ({ value: v, label: l }))} data-testid={`rcf-delai-${idx}`} size="lg" menuWidth={200}/>
        </div>
        <div className="col-span-full">
          <label className={lbl}>Description</label>
          <textarea rows={2} value={item.description} onChange={e => set("description", e.target.value)} data-testid={`rcf-desc-${idx}`} className={inp} placeholder="Contexte, horaires, compétences attendues…"/>
        </div>
      </div>
    </div>
  );
}

export default function RecrutementRequestForm({ onClose, onSaved, source = "rh", defaultCorner = "", inline = false, onBack }) {
  const [corners, setCorners] = useState([]);
  const [items, setItems] = useState([{ ...emptyItem(), corner_key: defaultCorner }]);
  const [saving, setSaving] = useState(false);
  const [esc, setEsc] = useState(emptyEscalation());
  useEffect(() => { api.get("/rh/corners").then(r => setCorners(r.data || [])).catch(() => setCorners([])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (items.some(i => !i.corner_key || !i.poste)) { toast.error("Corner et type de poste requis pour chaque demande"); return; }
    if (esc.enabled && !esc.support_service) { toast.error("Choisissez le service support destinataire"); return; }
    setSaving(true);
    try {
      const r = await api.post("/rh/recrutements/batch", { items, source });
      toast.success(`${r.data.count} demande${r.data.count > 1 ? "s" : ""} de recrutement créée${r.data.count > 1 ? "s" : ""}`);
      if (esc.enabled) {
        const first = r.data.items?.[0];
        const t = await createEscalationTicket(esc, { source_type: "recrutement", source_id: first?.id, titre: `Demande de recrutement · ${items.map(i => i.poste).join(", ")}`, corner_key: first?.corner_key });
        if (t) toast.success(`Ticket support ${esc.support_service} créé`); else toast.error("Le ticket support n'a pas pu être créé");
      }
      onSaved?.(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de la création"); }
    setSaving(false);
  };
  const totalPostes = items.reduce((a, i) => a + (i.nb_postes || 0), 0);

  const panel = (
      <div onClick={e => e.stopPropagation()} className={`bg-white border-slate-200 flex flex-col ${inline ? "min-h-0" : "rounded-2xl w-full max-w-3xl border shadow-xl max-h-[92vh]"}`} data-testid="rcf-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {onBack && <button type="button" onClick={onBack} data-testid="rcf-back" className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><ChevronLeft className="w-4 h-4"/></button>}
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 grid place-items-center"><UserPlus className="w-4 h-4"/></div>
            <div><h2 className="font-display text-lg font-semibold">Demande de recrutement</h2><p className="text-[11px] text-slate-500">{items.length} demande{items.length > 1 ? "s" : ""} · {totalPostes} poste{totalPostes > 1 ? "s" : ""}</p></div>
          </div>
          <button onClick={onClose} data-testid="rcf-close" className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          <div className="p-6 space-y-3 overflow-y-auto">
            {items.map((it, i) => <ItemRow key={i} idx={i} item={it} corners={corners} canRemove={items.length > 1}
                                           onChange={n => setItems(items.map((x, j) => j === i ? n : x))} onRemove={() => setItems(items.filter((_, j) => j !== i))}/>)}
            <button type="button" onClick={() => setItems([...items, { ...emptyItem(), corner_key: items[items.length - 1]?.corner_key || "" }])} data-testid="rcf-add-item"
                    className="w-full border-2 border-dashed border-slate-200 rounded-xl py-2.5 text-sm font-semibold text-blue-600 hover:border-blue-300 hover:bg-blue-50/40 inline-flex items-center justify-center gap-1.5">
              <Plus className="w-4 h-4"/> Ajouter une demande de recrutement
            </button>
            <SupportEscalationSection value={esc} onChange={setEsc} testid="rcf-esc"/>
          </div>
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
            <button type="submit" disabled={saving} data-testid="rcf-submit" className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 flex items-center gap-1.5">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin"/>} Envoyer {items.length > 1 ? `les ${items.length} demandes` : "la demande"}
            </button>
          </div>
        </form>
      </div>
  );
  if (inline) return panel;
  return (
    <div className="fixed inset-0 z-[1200] bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      {panel}
    </div>
  );
}
